import { useEffect, useState } from "react"
import { Head, router } from "@inertiajs/react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { MainContainer } from "@/components/ui/container"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { StaffLayout } from "@/components/layouts/staff-layout"
import { OrganizationWorkspaceNav } from "@/features/staff/organization/workspace-nav"
import { t } from "@/lib/i18n"

/**
 * The group's commercial identity: who it is on paper, and where to reach it.
 *
 * **This is not what stamps a CFDI.** `Location::TaxProfile`, edited at
 * `/manage/tax_profile`, is — it is unique per restaurant and holds the PAC
 * credentials, because the issuer of an invoice is the restaurant that served
 * the food. The two are independent and unlinked, which the note under the
 * fiscal block says out loud rather than leaving an owner to discover after
 * filling this in and finding their invoices unchanged.
 *
 * An explicit submit, not inline commits: an RFC saved halfway through typing is
 * a wrong RFC on file.
 */

type Settings = {
  name: string
  legalBusinessName: string | null
  rfc: string | null
  taxRegime: string | null
  postalCode: string | null
  addressLine1: string | null
  addressLine2: string | null
  city: string | null
  state: string | null
  country: string | null
  supportEmail: string | null
  supportPhone: string | null
}

type Props = {
  organization: { id: number; name: string }
  canManageOrganization: boolean
  settings: Settings
  taxRegimes: { value: string; label: string }[]
  errors?: Record<string, string> | null
}

const tSettings = (key: string) => t(`ui.react.pages.staff.location.organization.settings.${key}`)

function Field({
  name,
  value,
  onChange,
  error,
  type = "text",
}: {
  name: string
  value: string
  onChange: (value: string) => void
  error?: string
  type?: string
}) {
  const id = `organization-${name}`

  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id} className="text-muted-foreground text-xs">
        {tSettings(`fields.${name}`)}
      </Label>
      <Input
        id={id}
        type={type}
        value={value}
        aria-invalid={error ? true : undefined}
        data-testid={id}
        onChange={(event) => onChange(event.target.value)}
      />
      {error && (
        <p className="text-destructive text-xs" data-testid={`${id}-error`}>
          {error}
        </p>
      )}
    </div>
  )
}

export default function StaffLocationOrganizationSettings({
  organization,
  canManageOrganization,
  settings,
  taxRegimes,
  errors,
}: Props) {
  const [form, setForm] = useState({
    name: settings.name ?? "",
    legal_business_name: settings.legalBusinessName ?? "",
    rfc: settings.rfc ?? "",
    tax_regime: settings.taxRegime ?? "",
    postal_code: settings.postalCode ?? "",
    address_line1: settings.addressLine1 ?? "",
    address_line2: settings.addressLine2 ?? "",
    city: settings.city ?? "",
    state: settings.state ?? "",
    country: settings.country ?? "",
    support_email: settings.supportEmail ?? "",
    support_phone: settings.supportPhone ?? "",
  })

  // Re-seed from the server after a save. Inertia keeps this component mounted
  // across a visit to the same page, so the state initialised above survives the
  // redirect -- and the field would go on showing the `gfi950203abc` that was
  // typed while the database holds the `GFI950203ABC` that was stored. A form
  // that disagrees with what was saved is worse than one that clears.
  useEffect(() => {
    setForm({
      name: settings.name ?? "",
      legal_business_name: settings.legalBusinessName ?? "",
      rfc: settings.rfc ?? "",
      tax_regime: settings.taxRegime ?? "",
      postal_code: settings.postalCode ?? "",
      address_line1: settings.addressLine1 ?? "",
      address_line2: settings.addressLine2 ?? "",
      city: settings.city ?? "",
      state: settings.state ?? "",
      country: settings.country ?? "",
      support_email: settings.supportEmail ?? "",
      support_phone: settings.supportPhone ?? "",
    })
  }, [settings])

  const set = (key: keyof typeof form) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value }))

  const title = tSettings("page_title")

  return (
    <>
      <Head title={`${title} · ${organization.name}`} />

      <MainContainer size="md">
        <OrganizationWorkspaceNav
          current="settings"
          organizationName={organization.name}
          canManageOrganization={canManageOrganization}
        />

        <form
          className="space-y-4"
          data-testid="organization-settings-form"
          onSubmit={(event) => {
            event.preventDefault()
            router.patch("/organization/settings", form, { preserveScroll: true })
          }}
        >
          <Card density="compact">
            <CardHeader className="border-b">
              <CardTitle>{tSettings("identity_title")}</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <Field name="name" value={form.name} onChange={set("name")} error={errors?.name} />
              <Field
                name="legal_business_name"
                value={form.legal_business_name}
                onChange={set("legal_business_name")}
                error={errors?.legal_business_name}
              />
            </CardContent>
          </Card>

          <Card density="compact">
            <CardHeader className="border-b">
              <CardTitle>{tSettings("fiscal_title")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Said out loud, because the alternative is an owner filling this
                  in, issuing an invoice, and finding somebody else's RFC on it. */}
              <p className="text-muted-foreground text-xs" data-testid="organization-fiscal-note">
                {tSettings("fiscal_note")}
              </p>

              <div className="grid gap-4 md:grid-cols-2">
                <Field name="rfc" value={form.rfc} onChange={set("rfc")} error={errors?.rfc} />

                <div className="flex flex-col gap-1">
                  <Label htmlFor="organization-tax_regime" className="text-muted-foreground text-xs">
                    {tSettings("fields.tax_regime")}
                  </Label>
                  <Select value={form.tax_regime} onValueChange={set("tax_regime")}>
                    <SelectTrigger
                      id="organization-tax_regime"
                      className="w-full"
                      aria-invalid={errors?.tax_regime ? true : undefined}
                      data-testid="organization-tax_regime"
                    >
                      <SelectValue placeholder={tSettings("tax_regime_placeholder")} />
                    </SelectTrigger>
                    <SelectContent>
                      {taxRegimes.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                          data-testid={`organization-tax_regime-option-${option.value}`}
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors?.tax_regime && (
                    <p className="text-destructive text-xs" data-testid="organization-tax_regime-error">
                      {errors.tax_regime}
                    </p>
                  )}
                </div>

                <Field
                  name="postal_code"
                  value={form.postal_code}
                  onChange={set("postal_code")}
                  error={errors?.postal_code}
                />
              </div>
            </CardContent>
          </Card>

          <Card density="compact">
            <CardHeader className="border-b">
              <CardTitle>{tSettings("address_title")}</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <Field name="address_line1" value={form.address_line1} onChange={set("address_line1")} />
              <Field name="address_line2" value={form.address_line2} onChange={set("address_line2")} />
              <Field name="city" value={form.city} onChange={set("city")} />
              <Field name="state" value={form.state} onChange={set("state")} />
              <Field name="country" value={form.country} onChange={set("country")} />
            </CardContent>
          </Card>

          <Card density="compact">
            <CardHeader className="border-b">
              <CardTitle>{tSettings("contact_title")}</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <Field
                name="support_email"
                type="email"
                value={form.support_email}
                onChange={set("support_email")}
                error={errors?.support_email}
              />
              <Field name="support_phone" value={form.support_phone} onChange={set("support_phone")} />
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button type="submit" className="h-11 md:h-9" data-testid="organization-settings-submit">
              {tSettings("save")}
            </Button>
          </div>
        </form>
      </MainContainer>
    </>
  )
}


StaffLocationOrganizationSettings.layout = (page: React.ReactNode) => <StaffLayout>{page}</StaffLayout>
